import React, { createContext, useContext, useState, useRef, ReactNode, useEffect, useMemo } from 'react';
import { useViewSize } from './ViewSizeContext';

interface CarouselOffsetContextType {
  offsetLeft: number;
  updateContainerEl: (el?: HTMLElement) => void;
  updateOffsetLeft: () => void;
  setElementSelector: (selector: string) => void;
}

const CarouselOffsetContext = createContext<CarouselOffsetContextType | undefined>(undefined);

interface CarouselOffsetProviderProps {
  children: ReactNode;
  elementSelector?: string;
}

export const CarouselOffsetProvider: React.FC<CarouselOffsetProviderProps> = ({ 
  children, 
  elementSelector = '.carousel-paging'
}) => {
  const [offsetLeft, setOffsetLeft] = useState(0);
  const selector = useRef(elementSelector);
  const containerEl = useRef<HTMLElement | null>(null);
  const { viewWidth } = useViewSize();

  const updateOffsetLeft = () => {
    if (containerEl.current) {
      const rect = containerEl.current.getBoundingClientRect();
      setOffsetLeft(rect.left);
    }
  };

  const updateContainerEl = (el?: HTMLElement) => {
    const element = el || document.querySelector(selector.current) as HTMLElement | null;
    if (element) {
      containerEl.current = element;
      updateOffsetLeft();
    }
  };

  const setElementSelector = (newSelector: string) => {
    selector.current = newSelector;
    updateContainerEl();
  };

  useEffect(() => {
    updateOffsetLeft();
  }, [viewWidth]);

  const value = useMemo(() => ({
    offsetLeft,
    updateOffsetLeft,
    updateContainerEl,
    setElementSelector
  }), [offsetLeft]);

  return (
    <CarouselOffsetContext.Provider value={value}>
      {children}
    </CarouselOffsetContext.Provider>
  );
};

export const useCarouselOffset = () => {
  const context = useContext(CarouselOffsetContext);
  return context || {
    offsetLeft: 0,
    updateOffsetLeft: () => {},
    updateContainerEl: () => {},
    setElementSelector: () => {}
  };
};